import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  doubtfulDebtReserves,
  type DoubtfulDebtReserveItem,
} from '@/api/doubtfulDebtReserves';
import { contractors as contractorsApi, orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DocumentToolbar from '@/shared/components/DocumentToolbar';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  expense_account_code: string;
  reserve_account_code: string;
  comment: string;
  items: DoubtfulDebtReserveItem[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  expense_account_code: '944',
  reserve_account_code: '381',
  comment: '',
  items: [],
};

const newRow = (n: number): DoubtfulDebtReserveItem => ({
  line_number: n,
  contractor_id: '',
  contract_id: '',
  debt_account_code: '361',
  debt_amount: '0',
  reserve_percent: '100',
  reserve_amount: '0',
});

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function DoubtfulDebtReserveEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр org dropdown по accounting.manual.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('accounting.manual.manage');
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 1000 }),
  });
  const docQuery = useQuery({
    queryKey: ['doubtful-debt-reserve', id],
    queryFn: () => doubtfulDebtReserves.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const numberPreview = useNumberPreview('doc.doubtful_debt_reserves', form.organization_id, isNew);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f, organization_id: firstOrg,
        items: f.items.length ? f.items : [newRow(1)],
      });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        expense_account_code: d.expense_account_code,
        reserve_account_code: d.reserve_account_code,
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          line_number: it.line_number,
          contractor_id: it.contractor_id,
          contract_id: it.contract_id,
          debt_account_code: it.debt_account_code,
          debt_amount: String(it.debt_amount),
          reserve_percent: String(it.reserve_percent),
          reserve_amount: String(it.reserve_amount),
        })),
      });
    }
  }, [isNew, docQuery.data, availableOrgs]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  const uniqueContractorIds = useMemo(() => {
    const s = new Set<string>();
    form.items.forEach((it) => it.contractor_id && s.add(it.contractor_id));
    return Array.from(s);
  }, [form.items]);

  const contractQueries = useQueries({
    queries: uniqueContractorIds.map((cid) => ({
      queryKey: ['contracts', form.organization_id, cid],
      queryFn: () => refs.contracts({ organization_id: form.organization_id, contractor_id: cid }),
      enabled: !!form.organization_id && !!cid,
    })),
  });
  const contractsByContractor = useMemo(() => {
    const m: Record<string, { id: string; code: string; name: string }[]> = {};
    uniqueContractorIds.forEach((cid, idx) => {
      const q = contractQueries[idx];
      if (q?.data) m[cid] = q.data;
    });
    return m;
  }, [uniqueContractorIds, contractQueries]);

  const total = form.items.reduce((s, e) => s + Number(e.reserve_amount || 0), 0);

  const create = useMutation({
    mutationFn: () => doubtfulDebtReserves.create({
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      expense_account_code: form.expense_account_code,
      reserve_account_code: form.reserve_account_code,
      comment: form.comment || null,
      items: form.items,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['doubtful-debt-reserves'] });
      navigate(`/doubtful-debt-reserves/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => doubtfulDebtReserves.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      expense_account_code: form.expense_account_code,
      reserve_account_code: form.reserve_account_code,
      comment: form.comment || null,
      items: form.items,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['doubtful-debt-reserve', id] }),
    onError: (e) => setError(extractError(e)),
  });
  // Новий: зберегти чернетку і одразу провести (create → post → картка).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await doubtfulDebtReserves.create({
        organization_id: form.organization_id,
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        expense_account_code: form.expense_account_code,
        reserve_account_code: form.reserve_account_code,
        comment: form.comment || null,
        items: form.items,
      });
      await doubtfulDebtReserves.post(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['doubtful-debt-reserves'] });
      navigate(`/doubtful-debt-reserves/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений: зберегти поточні правки і одразу провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await doubtfulDebtReserves.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        expense_account_code: form.expense_account_code,
        reserve_account_code: form.reserve_account_code,
        comment: form.comment || null,
        items: form.items,
      });
      await doubtfulDebtReserves.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['doubtful-debt-reserve', id] });
      qc.invalidateQueries({ queryKey: ['doubtful-debt-reserves'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => doubtfulDebtReserves.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['doubtful-debt-reserve', id] });
      qc.invalidateQueries({ queryKey: ['doubtful-debt-reserves'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => doubtfulDebtReserves.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['doubtful-debt-reserves'] });
      navigate('/doubtful-debt-reserves');
    },
  });

  function setItem(i: number, patch: Partial<DoubtfulDebtReserveItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => {
        if (idx !== i) return e;
        const next = { ...e, ...patch };
        if ('contractor_id' in patch) next.contract_id = '';
        if ('debt_amount' in patch || 'reserve_percent' in patch) {
          const d = Number(next.debt_amount || 0);
          const p = Number(next.reserve_percent || 0);
          next.reserve_amount = ((d * p) / 100).toFixed(2);
        }
        return next;
      }),
    }));
  }
  function addRow() {
    setForm((f) => ({ ...f, items: [...f.items, newRow(f.items.length + 1)] }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  // UX β: warning якщо новий документ а user не має accounting.manual.manage у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: '#fef3c7', borderRadius: 6,
            border: '1px solid #fbbf24', color: '#92400e',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати резерв сумнівних боргів у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/doubtful-debt-reserves')}>Скасувати</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий резерв сумнівних боргів' : `Резерв: ${form.document_number}`}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ проведено
            </span>
          )}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate('/doubtful-debt-reserves')}
          onSave={() => isNew ? create.mutate() : update.mutate()}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          postDisabled={form.items.length === 0}
          onPost={() => isNew ? createAndPost.mutate() : updateAndPost.mutate()}
          postPending={create.isPending || createAndPost.isPending || update.isPending || updateAndPost.isPending}
          postTitle="Зберігає та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Організація</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field">
          <label>№ документа</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>Дата</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>Рах. витрат</label>
          <input value={form.expense_account_code}
                 onChange={(e) => setForm({ ...form, expense_account_code: e.target.value })}
                 disabled={isPosted} placeholder="944" />
        </div>
        <div className="field">
          <label>Рах. резерву</label>
          <input value={form.reserve_account_code}
                 onChange={(e) => setForm({ ...form, reserve_account_code: e.target.value })}
                 disabled={isPosted} placeholder="381" />
        </div>
      </div>

      <h3 style={{ marginTop: '1.5rem' }}>Сумнівні борги</h3>
      <p style={{ marginTop: '-0.5rem', color: '#64748b', fontSize: '0.85rem' }}>
        У кожному рядку — контрагент і його борг. Сума резерву = борг × процент / 100.
        При проведенні створюється проводка Дт &lt;рах. витрат&gt; — Кт &lt;рах. резерву&gt;
        з субконто контрагент+договір на кредиті.
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 30 }}>#</th>
            <th style={{ padding: '0.5rem' }}>Контрагент</th>
            <th style={{ padding: '0.5rem' }}>Договір</th>
            <th style={{ padding: '0.5rem', width: 80 }}>Рах. боргу</th>
            <th style={{ padding: '0.5rem', width: 130 }}>Сума боргу</th>
            <th style={{ padding: '0.5rem', width: 90 }}>%</th>
            <th style={{ padding: '0.5rem', width: 130 }}>Сума резерву</th>
            <th style={{ padding: '0.5rem', width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => {
            const contracts = contractsByContractor[it.contractor_id] ?? [];
            return (
              <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
                <td style={{ padding: '0.5rem' }}>{it.line_number}</td>
                <td style={{ padding: '0.5rem' }}>
                  <select value={it.contractor_id}
                          onChange={(e) => setItem(i, { contractor_id: e.target.value })}
                          disabled={isPosted} style={{ width: '100%' }}>
                    <option value="">—</option>
                    {contractorsQ.data?.items.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </td>
                <td style={{ padding: '0.5rem' }}>
                  <select value={it.contract_id}
                          onChange={(e) => setItem(i, { contract_id: e.target.value })}
                          disabled={isPosted || !it.contractor_id} style={{ width: '100%' }}>
                    <option value="">—</option>
                    {contracts.map((c) => (
                      <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
                    ))}
                  </select>
                </td>
                <td style={{ padding: '0.5rem' }}>
                  <input value={it.debt_account_code}
                         onChange={(e) => setItem(i, { debt_account_code: e.target.value })}
                         disabled={isPosted} style={{ width: '100%' }} />
                </td>
                <td style={{ padding: '0.5rem' }}>
                  <input type="number" step="0.01" value={it.debt_amount}
                         onChange={(e) => setItem(i, { debt_amount: e.target.value })}
                         disabled={isPosted} style={{ textAlign: 'right', width: '100%' }} />
                </td>
                <td style={{ padding: '0.5rem' }}>
                  <input type="number" step="0.01" value={it.reserve_percent}
                         onChange={(e) => setItem(i, { reserve_percent: e.target.value })}
                         disabled={isPosted} style={{ textAlign: 'right', width: '100%' }} />
                </td>
                <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>
                  {fmt(Number(it.reserve_amount))}
                </td>
                <td style={{ padding: '0.5rem' }}>
                  {!isPosted && (
                    <button onClick={() => removeRow(i)} className="danger"
                            style={{ padding: '0.3rem 0.6rem' }}>×</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 600 }}>
            <td colSpan={6} style={{ padding: '0.5rem', textAlign: 'right' }}>Усього резерв:</td>
            <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(total)}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
      {!isPosted && (
        <button onClick={addRow} style={{ marginTop: '0.75rem' }}>+ Додати рядок</button>
      )}

      <div className="field" style={{ marginTop: '1rem' }}>
        <label>Коментар</label>
        <input value={form.comment}
               onChange={(e) => setForm({ ...form, comment: e.target.value })}
               disabled={isPosted} />
      </div>

      {error && <div className="error">{error}</div>}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
